/**
 * Mobile-only bottom tab bar — **Chat · Ballot · My Info** — the phone
 * layout's primary navigation. Replaces the draggable ballot sheet that used
 * to dock on top of the chat composer.
 *
 * Keyboard behavior: the bar hides while the on-screen keyboard is up and
 * slides back the moment it closes. That is the platform convention (iOS tab
 * bars sit under the keyboard; Material's navigation bar hides for it), and
 * the alternatives are worse here: lifting the bar above the keyboard would
 * take another 56px from the ~half of the screen that is left, and a
 * substitute control in the app bar is unreliable on iOS Safari, which
 * scrolls the fixed header out of view when the keyboard opens. Tapping the
 * transcript or sending a message dismisses the keyboard and brings the tabs
 * back.
 *
 * A horizontal swipe anywhere on the screen also moves between the tabs
 * (``useMobileTabSwipe``), so the bar is the indicator and one of two ways to
 * navigate, as in a native pager.
 *
 * Publishes ``--mobile-tab-bar-height`` (56px, or 0px while hidden) so the
 * composer and the ballot panel can reserve room above the bar with one CSS
 * variable, the same way ``--keyboard-inset-px`` works.
 */
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ChatBubbleOvalLeftEllipsisIcon } from '@heroicons/react/24/outline';
import {
  BottomNavigation,
  BottomNavigationAction,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { useAuth } from '@/components/auth/AuthProvider';
import useAnalytics from '@/hooks/useAnalytics';
import { useCanvasBackground } from '@/hooks/useCanvasBackground';
import { useDirection } from '@/hooks/useDirection';
import { useMobileTabSwipe, type SwipeDirection } from '@/hooks/useMobileTabSwipe';
import { useBallotSheet, type MobileBallotTab } from '@/providers/BallotSheetProvider';
import { semanticColors } from '@/theme/tokens';
import BallotIcon from '@/ui-library/BallotIcon';
import CustomInfoShieldIcon from '@/ui-library/CustomInfoShieldIcon';

import { shouldHideMobileTabBar } from './mobileTabBarVisibility';

export const MOBILE_TAB_BAR_HEIGHT_PX = 56;
/** CSS variable consumers read to stay clear of the bar (0px while hidden). */
export const MOBILE_TAB_BAR_HEIGHT_VAR = '--mobile-tab-bar-height';

const TABS: ReadonlyArray<MobileBallotTab> = ['chat', 'ballot', 'info'];

const MobileTabBar: React.FC = () => {
  const { t } = useTranslation('common');
  const { activeTab, setActiveTab, keyboardUp, landingFullScreen } = useBallotSheet();
  const { isAuthenticated, user } = useAuth();
  const { track } = useAnalytics();
  // Same breakpoint as the ``display`` rule below (this theme's ``md``), so the
  // published height is 0 wherever the bar is not actually painted.
  const theme = useTheme();
  const isPhoneWidth = useMediaQuery(theme.breakpoints.down('md'));
  const dir = useDirection();
  // The guest landing owns the whole screen until an address exists: no tabs to
  // show yet (Ballot and My Info are both the same address entry), so the bar
  // and its swipe stand down and the landing reads as the standalone page.
  // Same for the post-auth name & address signup modal — Ballot Preview would
  // be empty until setup completes.
  const standDown = shouldHideMobileTabBar({
    landingFullScreen,
    isAuthenticated,
    initialSetupComplete: user?.initialSetupComplete,
  });
  const shown = isPhoneWidth && !keyboardUp && !standDown;

  // The bar paints the safe-area strip itself; match the canvas so Android's
  // edge-to-edge system-bar region reads the same white.
  useCanvasBackground(isPhoneWidth && !standDown ? semanticColors.surface : null);

  useEffect(() => {
    document.documentElement.style.setProperty(
      MOBILE_TAB_BAR_HEIGHT_VAR,
      `${shown ? MOBILE_TAB_BAR_HEIGHT_PX : 0}px`
    );
    return () => document.documentElement.style.removeProperty(MOBILE_TAB_BAR_HEIGHT_VAR);
  }, [shown]);

  const selectTab = (next: MobileBallotTab, via: 'tap' | 'swipe') => {
    if (!TABS.includes(next) || next === activeTab) return;
    track('Bottom Tab Selected', { tab: next, previousTab: activeTab, via });
    setActiveTab(next);
  };

  const handleChange = (_event: React.SyntheticEvent, next: MobileBallotTab) =>
    selectTab(next, 'tap');

  // Swipe anywhere on the screen moves one tab in the direction of travel.
  // ``dir="rtl"`` reverses the bar's flex row, so Chat sits on the right and
  // the later tabs run leftward; the gesture follows the pixels, which puts
  // "forward" on a leftward swipe in LTR and a rightward one in RTL. No
  // wrap-around.
  useMobileTabSwipe(isPhoneWidth && !standDown, (direction: SwipeDirection) => {
    const index = TABS.indexOf(activeTab);
    const forward = direction === (dir === 'rtl' ? 'right' : 'left');
    const next = TABS[forward ? index + 1 : index - 1];
    if (next) selectTab(next, 'swipe');
  });

  const labels: Record<MobileBallotTab, string> = {
    chat: t('bottomNav.chat'),
    ballot: t('bottomNav.ballot'),
    info: t('bottomNav.info'),
  };
  const icons: Record<MobileBallotTab, React.ReactNode> = {
    chat: <ChatBubbleOvalLeftEllipsisIcon width={24} height={24} aria-hidden="true" />,
    ballot: <BallotIcon size={24} />,
    info: <CustomInfoShieldIcon size={22} />,
  };

  if (standDown) return null;

  return (
    <Box
      component="nav"
      aria-label={t('bottomNav.ariaLabel')}
      aria-hidden={keyboardUp || undefined}
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        // Mobile-only. On md+ the ballot lives in the right-side panel.
        display: { xs: 'block', md: 'none' },
        zIndex: (theme) => theme.zIndex.appBar,
        backgroundColor: semanticColors.surface,
        borderTop: `1px solid ${semanticColors.borderLight}`,
        // Own the home-indicator strip so the tabs sit above it.
        paddingBottom: 'env(safe-area-inset-bottom)',
        // Slide out under the keyboard, slide back when it closes.
        transform: keyboardUp ? 'translateY(100%)' : 'translateY(0)',
        visibility: keyboardUp ? 'hidden' : 'visible',
        transition: keyboardUp
          ? 'transform 120ms ease-in, visibility 0s linear 120ms'
          : 'transform 180ms ease-out',
        touchAction: 'manipulation',
      }}
    >
      <BottomNavigation
        showLabels
        value={activeTab}
        onChange={handleChange}
        sx={{
          height: MOBILE_TAB_BAR_HEIGHT_PX,
          backgroundColor: 'transparent',
        }}
      >
        {TABS.map((tab) => (
          <BottomNavigationAction
            key={tab}
            value={tab}
            label={labels[tab]}
            icon={icons[tab]}
            aria-current={activeTab === tab ? 'page' : undefined}
            // Lets ``MobileBallotPanel`` hand focus to the Chat tab when a
            // control inside the ballot content switches back to Chat.
            data-vm-tab={tab}
            disableRipple
            sx={{
              minWidth: 0,
              padding: '6px 8px 8px',
              gap: '2px',
              color: semanticColors.textTertiary,
              '&.Mui-selected': { color: semanticColors.textPrimary },
              '& .MuiBottomNavigationAction-label': {
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 12,
                fontWeight: 500,
                lineHeight: 1.2,
                '&.Mui-selected': { fontSize: 12, fontWeight: 700 },
              },
              '&:focus-visible': {
                outline: `2px solid ${semanticColors.brandPrimary}`,
                outlineOffset: '-3px',
                borderRadius: '8px',
              },
            }}
          />
        ))}
      </BottomNavigation>
    </Box>
  );
};

export default MobileTabBar;
